import React from "react";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";

const copy = {
  nl: {
    welcome: "Welkom",
    title: "The Data Literacy Card Game",
    language: "Taal",
    dutchOption: "Nederlands",
    englishOption: "English",
    intro:
      "Een AI kan alleen de mensen zien die in de data zichtbaar zijn. Geef het systeem andere databronnen en er ontstaat een andere synthetische gebruiker, terwijl sommige mensen juist uit beeld verdwijnen. In dit spel ervaar je dat verschil zelf.",
    placemat:
      "Voor je ligt de A3-placemat en drie stapels kaarten. Elke kaart heeft een code, zoals DS-01, die je in deze app invoert:",
    dataTitle: "Databronkaarten",
    dataText: "Wat de AI weet over je gebruikers. Andere data betekent een ander perspectief.",
    biasTitle: "Biaskaarten",
    biasText: "Wie ontbreekt in de data, en daardoor ook in het beeld van de AI.",
    modeTitle: "Responsmoduskaart",
    modeText: "Hoe je synthetische gebruiker denkt, handelt en reageert.",
    plan:
      "Eerst beschrijf je het ontwerp waarop je feedback wilt. Daarna speel je twee rondes. In elke ronde kies je een nieuwe combinatie kaarten en bouwt de app daar een synthetische gebruiker van. Daarna vergelijk je beide outputs en reflecteer je: wat veranderde, wat miste de AI, en wie werd buitengesloten?",
    ready: "Klaar?",
    readyText: "Begin met het beschrijven van je ontwerpconcept.",
    begin: "Beginnen ->",
  },
  en: {
    welcome: "Welcome",
    title: "The Data Literacy Card Game",
    language: "Language",
    dutchOption: "Dutch",
    englishOption: "English",
    intro:
      "An AI can only ever see the people hidden in its data. Feed it one set of sources and it imagines one kind of user; change the data and a completely different person appears, while others quietly disappear. In this game you get to feel that shift for yourself.",
    placemat:
      "On the A3 placemat in front of you are three stacks of cards. Each card has a code, like DS-01, that you type into this app:",
    dataTitle: "Data Source cards",
    dataText: "What the AI knows about your users. Different data, different perspective.",
    biasTitle: "Bias cards",
    biasText: "Who is missing from that data, and therefore from the AI's view.",
    modeTitle: "Response mode card",
    modeText: "How your synthetic user thinks, acts, and reacts.",
    plan:
      "First you describe the design you want feedback on. Then you play two rounds. Each round you pick a fresh combination of cards and the app builds a synthetic user from them. Afterwards you compare both outputs and reflect: what changed, what did the AI miss, and who got left out?",
    ready: "Ready?",
    readyText: "Start by describing your design concept.",
    begin: "Let's begin ->",
  },
};

export function IntroStep({ language = "nl", setLanguage, onNext }) {
  const t = copy[language] || copy.nl;

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-black/10 bg-white p-6">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[rgba(144,92,255,0.12)] text-[#905cff]">
              <Sparkles className="h-5 w-5" />
            </span>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#905cff] font-medium">{t.welcome}</div>
              <h2 className="font-display text-xl font-semibold text-black">{t.title}</h2>
            </div>
          </div>
        </div>

        <div className="mb-5">
          <label className="block text-sm font-medium text-black mb-1">{t.language}</label>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={language === "nl" ? "default" : "outline"}
              onClick={() => setLanguage("nl")}
              className={`rounded-xl ${language === "nl" ? "bg-[#905cff] text-white hover:bg-[#5802ff]" : ""}`}
            >
              {t.dutchOption}
            </Button>
            <Button
              type="button"
              variant={language === "en" ? "default" : "outline"}
              onClick={() => setLanguage("en")}
              className={`rounded-xl ${language === "en" ? "bg-[#905cff] text-white hover:bg-[#5802ff]" : ""}`}
            >
              {t.englishOption}
            </Button>
          </div>
        </div>

        <div className="space-y-4 text-sm text-black/70 leading-relaxed">
          <p>{t.intro}</p>
          <p>{t.placemat}</p>

          <div className="rounded-xl border border-black/8 bg-[#F8F8F8] p-4 space-y-3">
            <CardType badge="DS" color="#5803fd" title={t.dataTitle} text={t.dataText} />
            <CardType badge="BI" color="#e85e0b" title={t.biasTitle} text={t.biasText} />
            <CardType badge="RM" color="#63cba4" title={t.modeTitle} text={t.modeText} />
          </div>

          <p>{t.plan}</p>

          <div className="rounded-xl border border-[#905cff]/30 bg-[rgba(144,92,255,0.06)] p-3 text-sm">
            <strong className="text-black">{t.ready}</strong> {t.readyText}
          </div>
        </div>
      </div>

      <Button onClick={onNext} className="w-full rounded-xl bg-[#905cff] text-white hover:bg-[#5802ff] py-3">
        {t.begin}
      </Button>
    </div>
  );
}

function CardType({ badge, color, title, text }) {
  return (
    <div className="flex items-start gap-3">
      <span
        className="mt-0.5 h-5 w-7 rounded-full text-white text-[10px] font-bold flex items-center justify-center shrink-0"
        style={{ backgroundColor: color }}
      >
        {badge}
      </span>
      <div>
        <div className="font-medium text-black">{title}</div>
        <div className="text-xs text-black/50 mt-0.5">{text}</div>
      </div>
    </div>
  );
}
